// Файл: ZeroLagChaikinOscillator.ts

// --- Вспомогательные классы индикаторов ---

/**
 * Простая скользящая средняя (Simple Moving Average)
 */
class SMA {
  values = []
  sum = 0

  constructor(period) {
    this.period = Math.max(1, period) // Период не может быть меньше 1
  }

  nextValue(value) {
    this.values.push(value)
    this.sum += value

    if (this.values.length > this.period) {
      this.sum -= this.values.shift() // Удаляем самое старое значение
    }

    if (this.values.length < this.period) {
      // Возвращаем среднее от имеющихся значений, пока не набрался полный период
      return this.sum / this.values.length
    }

    return this.sum / this.period
  }

  momentValue(value) {
    if (this.values.length < this.period - 1) {
      return (this.sum + value) / (this.values.length + 1)
    }
    const newValues = [...this.values, value]
    if (newValues.length > this.period) {
      newValues.shift()
    }
    const momentSum = newValues.reduce((a, b) => a + b, 0)
    return momentSum / newValues.length
  }
}

/**
 * Экспоненциальная скользящая средняя (Exponential Moving Average)
 */
class EMA {
  lastEma = null

  constructor(period) {
    this.period = Math.max(1, period)
    this.alpha = 2 / (this.period + 1)
  }

  nextValue(value) {
    if (this.lastEma === null) {
      this.lastEma = value
    } else {
      this.lastEma = this.alpha * value + (1 - this.alpha) * this.lastEma
    }
    return this.lastEma
  }

  momentValue(value) {
    const lastEma = this.lastEma ?? value
    return this.alpha * value + (1 - this.alpha) * lastEma
  }
}

/**
 * Zero-Lag Экспоненциальная скользящая средняя (Zero-Lag EMA)
 */
class ZLEMA {
  history = []

  constructor(period) {
    this.period = Math.max(1, period)
    this.internalEma = new EMA(period)
  }

  nextValue(source) {
    this.history.push(source)
    if (this.history.length > this.period) {
      this.history.shift()
    }

    // Пока не набрано достаточно истории, работаем как обычная EMA
    if (this.history.length < this.period) {
      return this.internalEma.nextValue(source)
    }

    const momentum = source - this.history[0]
    const deLaggedSource = source + momentum

    return this.internalEma.nextValue(deLaggedSource)
  }

  momentValue(source) {
    if (this.history.length < this.period) {
      return this.internalEma.momentValue(source)
    }
    const momentum = source - this.history[1] // [1] потому что текущее значение еще не в истории
    const deLaggedSource = source + momentum
    return this.internalEma.momentValue(deLaggedSource)
  }
}

// --- Основной класс индикатора ---

/**
 * Измеряет моментум Линии Аккумуляции/Распределения (A/D), используя ZLEMA и опциональное сглаживание SMA.
 * Это JS/TS-версия индикатора "ZL Chaikin Oscillator (Smoothed)" из Pine Script.
 */
export class ZeroLagChaikinOscillator {
  accDistribution = 0

  constructor(fastPeriod = 3, slowPeriod = 10, smoothPeriod = 3) {
    this.zlemaFast = new ZLEMA(fastPeriod)
    this.zlemaSlow = new ZLEMA(slowPeriod)
    this.smoother = new SMA(smoothPeriod)
  }

  /**
   * Вычисляет следующее значение осциллятора на основе новой свечи.
   * @param h - High (максимум)
   * @param l - Low (минимум)
   * @param c - Close (цена закрытия)
   * @param v - Volume (объем)
   * @returns Сглаженное значение Zero-Lag Chaikin Oscillator
   */
  nextValue(h, l, c, v) {
    // 1. Обновляем Линию Аккумуляции/Распределения
    // Формула взята из вашего примера для полной совместимости
    this.accDistribution +=
      (c === h && c === l) || h === l ? 0 : ((2 * c - l - h) / (h - l)) * v

    // 2. Вычисляем "сырое" значение осциллятора
    const rawOsc =
      this.zlemaFast.nextValue(this.accDistribution) -
      this.zlemaSlow.nextValue(this.accDistribution)

    // 3. Сглаживаем и возвращаем результат
    return this.smoother.nextValue(rawOsc)
  }

  /**
   * Вычисляет "мгновенное" значение для текущей, еще не закрытой свечи, не изменяя внутреннее состояние.
   */
  momentValue(h, l, c, v) {
    // 1. Вычисляем гипотетическую A/D Line
    const momentAccDistribution =
      this.accDistribution +
      ((c === h && c === l) || h === l ? 0 : ((2 * c - l - h) / (h - l)) * v)

    // 2. Вычисляем гипотетическое "сырое" значение осциллятора
    const rawOsc =
      this.zlemaFast.momentValue(momentAccDistribution) -
      this.zlemaSlow.momentValue(momentAccDistribution)

    // 3. Сглаживаем и возвращаем гипотетический результат
    return this.smoother.momentValue(rawOsc)
  }
}
